import React from 'react';
import './LoginHero.css';

/**
 * Ambient hero panel for the Login / Register / Landing pages. Pure CSS +
 * inline SVG — no JS animation loop, no extra deps, no network calls.
 *
 * Intentionally shows only **symbol tickers + abstract price motion** — no
 * rupee values, no portfolio totals, no P/L figures. The goal is to evoke
 * "Indian equities, clearly tracked" without exposing any logged-out visitor
 * to numbers that might look stale or dishonest.
 */
export const LoginHero = () => {
  const featured = {
    sym: 'RELIANCE',
    name: 'Reliance Industries',
    change: '+1.62%',
    up: true,
  };

  const tiles = [
    { sym: 'TCS',        change: '-0.47%', up: false },
    { sym: 'INFY',       change: '+0.94%', up: true  },
    { sym: 'HDFCBANK',   change: '+0.18%', up: true  },
    { sym: 'ITC',        change: '-0.22%', up: false },
    { sym: 'SBIN',       change: '+1.03%', up: true  },
    { sym: 'BHARTIARTL', change: '+0.65%', up: true  },
  ];

  return (
    <div
      data-testid="login-hero"
      className="relative h-full w-full overflow-hidden"
      style={{
        backgroundColor: '#0E2A20',
        backgroundImage:
          'radial-gradient(120% 80% at 10% 10%, #1B4332 0%, #0E2A20 55%, #07180F 100%)',
      }}
    >
      {/* Subtle gold dot grid */}
      <svg
        className="absolute inset-0 h-full w-full eqt-grid"
        aria-hidden="true"
        width="100%"
        height="100%"
      >
        <defs>
          <pattern id="eqt-dots" width="28" height="28" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#D4AF37" fillOpacity="0.15" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#eqt-dots)" />
      </svg>

      {/* Brand wordmark — top */}
      <div className="absolute" style={{ top: '6%', left: '8%', right: '8%' }}>
        <p
          className="text-xs font-medium uppercase"
          style={{ color: '#A9C8B6', letterSpacing: '0.18em' }}
        >
          EquiTrack
        </p>
        <p
          className="text-2xl mt-1.5 leading-snug max-w-sm"
          style={{ color: '#F7F7F5', fontFamily: 'Work Sans' }}
        >
          Indian equities,
          <br />
          <span style={{ color: '#A8E6C1' }}>clearly tracked.</span>
        </p>
        {/* Feature spotlight pill — quietly advertises Tax Harvesting on
            every login / register / forgot-password page. Stays visually
            consistent with the calm brand palette. */}
        <div
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase"
          style={{
            backgroundColor: 'rgba(168,230,193,0.12)',
            color: '#A8E6C1',
            border: '1px solid rgba(168,230,193,0.28)',
            letterSpacing: '0.16em',
          }}
          data-testid="login-hero-tax-spotlight"
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
          Now · Tax Harvesting · Save up to ₹1.25L
        </div>
      </div>

      {/* Featured ticker + large chart */}
      <div
        className="absolute eqt-tile"
        style={{
          top: '34%',
          left: '8%',
          right: '8%',
          padding: '24px 26px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-sm font-medium" style={{ color: '#F7F7F5' }}>
              {featured.sym}
              <span className="ml-2 text-xs font-normal" style={{ color: '#A9C8B6' }}>
                {featured.name}
              </span>
            </p>
            <p
              className="text-[11px] mt-1 uppercase tracking-[0.14em]"
              style={{ color: '#6E8B7A' }}
            >
              NSE · EOD
            </p>
          </div>
          <div
            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: featured.up ? 'rgba(123, 207, 166, 0.14)' : 'rgba(231, 166, 154, 0.14)',
              color: featured.up ? '#7BCFA6' : '#E7A69A',
              border: `1px solid ${featured.up ? 'rgba(123, 207, 166, 0.3)' : 'rgba(231, 166, 154, 0.3)'}`,
            }}
          >
            {featured.change}
          </div>
        </div>

        {/* Featured chart (RELIANCE) */}
        <div style={{ height: 110 }}>
          <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="h-full w-full" aria-hidden="true">
            <defs>
              <linearGradient id="eqt-area" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#7BCFA6" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#7BCFA6" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="eqt-stroke" x1="0" x2="1">
                <stop offset="0%" stopColor="#A8E6C1" />
                <stop offset="100%" stopColor="#7BCFA6" />
              </linearGradient>
            </defs>
            {[30, 60, 90].map((y) => (
              <line
                key={y}
                x1="0" x2="400" y1={y} y2={y}
                stroke="#FFFFFF" strokeOpacity="0.06" strokeDasharray="3 4"
              />
            ))}
            <path
              d="M0,94 C40,86 60,78 90,70 C120,62 140,72 170,58 C200,46 220,56 250,38 C280,22 310,32 340,20 C370,10 388,16 400,12 L400,120 L0,120 Z"
              fill="url(#eqt-area)"
            />
            <path
              className="eqt-chart-line"
              d="M0,94 C40,86 60,78 90,70 C120,62 140,72 170,58 C200,46 220,56 250,38 C280,22 310,32 340,20 C370,10 388,16 400,12"
              fill="none"
              stroke="url(#eqt-stroke)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle className="eqt-chart-dot" cx="400" cy="12" r="5" fill="#A8E6C1" />
            <circle cx="400" cy="12" r="3" fill="#F7F7F5" />
          </svg>
        </div>
        {/* NOTE: 52-week range rail intentionally removed — no numeric values
            shown on logged-out hero per product decision. */}
      </div>

      {/* Ticker grid — 3x2 */}
      <div className="absolute" style={{ left: '8%', right: '8%', bottom: '7%' }}>
        <p
          className="text-xs font-medium uppercase mb-3"
          style={{ color: '#A9C8B6', letterSpacing: '0.12em' }}
        >
          Markets Today
        </p>
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((t, i) => (
            <div
              key={t.sym}
              className={`eqt-tile eqt-tile-${(i % 4) + 1}`}
              style={{
                padding: '12px 14px',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.07)',
              }}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium" style={{ color: '#F7F7F5' }}>
                  {t.sym}
                </span>
                <span
                  className="text-[11px] font-medium"
                  style={{ color: t.up ? '#7BCFA6' : '#E7A69A' }}
                >
                  {t.change}
                </span>
              </div>
              {/* Miniature sparkline per tile — no numeric value shown */}
              <svg viewBox="0 0 80 20" preserveAspectRatio="none" className="mt-3 w-full" height="22">
                <polyline
                  fill="none"
                  stroke={t.up ? '#7BCFA6' : '#E7A69A'}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  points={
                    t.up
                      ? (i % 2 === 0
                          ? '0,16 12,14 24,15 36,11 48,12 60,7 72,8 80,4'
                          : '0,15 12,13 24,10 36,12 48,8 60,9 72,6 80,5')
                      : (i % 2 === 0
                          ? '0,6 12,8 24,7 36,11 48,10 60,13 72,12 80,16'
                          : '0,5 12,7 24,10 36,9 48,12 60,11 72,14 80,15')
                  }
                />
              </svg>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoginHero;
